import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import DatePicker from './DatePicker';
import { parseCalendarDate, toCalendarDateString } from '../lib/calendar-date';

interface TimeOffDatesModalProps {
  /** Current start of the request, in any calendar-day shape the API sends. */
  startDate: string | null | undefined;
  /** Current end of the request, in any calendar-day shape the API sends. */
  endDate: string | null | undefined;
  isPending?: boolean;
  /** Message from a failed save, rendered above the buttons. */
  error?: string | null;
  onCancel: () => void;
  onSave: (dates: { startDate: string; endDate: string }) => void;
}

/**
 * Re-date an existing time-off request. Both bounds are calendar days, so the
 * incoming value is normalised through `parseCalendarDate` before it reaches
 * `DatePicker` — a raw 'YYYY-MM-DDT00:00:00Z' would otherwise prefill the day
 * before west of Greenwich. @see src/lib/calendar-date.
 */
export default function TimeOffDatesModal({
  startDate,
  endDate,
  isPending = false,
  error,
  onCancel,
  onSave,
}: TimeOffDatesModalProps) {
  const normalize = (v: string | null | undefined) => {
    const parsed = parseCalendarDate(v);
    return parsed ? toCalendarDateString(parsed) : '';
  };

  const [start, setStart] = useState(() => normalize(startDate));
  const [end, setEnd] = useState(() => normalize(endDate));

  // 'YYYY-MM-DD' sorts lexicographically, so a string compare is a date compare.
  const outOfOrder = Boolean(start && end && end < start);
  const canSave = Boolean(start) && Boolean(end) && !outOfOrder && !isPending;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-fieldiq-card rounded-xl p-5 w-full max-w-sm space-y-3" onClick={e => e.stopPropagation()}>
        <h3 className="font-semibold text-white">Edit Dates</h3>

        <div>
          <label className="text-xs text-fieldiq-text mb-1 block">Start Date</label>
          <DatePicker value={start} onChange={setStart} placeholder="Start" />
        </div>

        <div>
          <label className="text-xs text-fieldiq-text mb-1 block">End Date</label>
          <DatePicker value={end} onChange={setEnd} placeholder="End" />
        </div>

        {outOfOrder && (
          <p className="text-red-400 text-xs">End date cannot be before the start date.</p>
        )}

        {error && <p className="text-red-400 text-xs">{error}</p>}

        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="flex-1 py-2 text-sm rounded-lg bg-fieldiq-dark text-fieldiq-text"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave({ startDate: start, endDate: end })}
            disabled={!canSave}
            className="flex-1 py-2 text-sm rounded-lg bg-fieldiq-accent text-fieldiq-dark font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isPending && <Loader2 size={14} className="animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
